/**
 * Atmosphere for the Alpha warehouse: dust motes, light curtains under the lit
 * tubes, and the cinematic post stack (bloom -> tone map -> lens grade with
 * optional fringing, vignette and a light grain -> FXAA).
 *
 * Everything here is GPU-animated from a single `time` uniform, so the CPU
 * cost per frame is constant no matter how many motes there are.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';

const SHAFT_GLSL = (n) => `
uniform vec3 shafts[${n}];
float shaftLight( vec3 p ) {
  float l = 0.0;
  for ( int i = 0; i < ${n}; i ++ ) {
    float d = length( p.xz - shafts[ i ].xy );
    l = max( l, smoothstep( shafts[ i ].z, 0.0, d ) );
  }
  return l;
}`;

const shaftUniform = (shafts) => ({ value: shafts.map((s) => new THREE.Vector3(s.x, s.z, s.r)) });

/** Slow dust motes that glint when they drift through a shaft. */
export function createDust(count, box, shafts, shared, pixelRatio) {
  const size = box.getSize(new THREE.Vector3());
  const pos = new Float32Array(count * 3);
  const seed = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    pos.set(
      [box.min.x + Math.random() * size.x, box.min.y + Math.random() * size.y, box.min.z + Math.random() * size.z],
      i * 3,
    );
    seed[i] = Math.random();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));

  const mat = new THREE.ShaderMaterial({
    uniforms: { ...shared, shafts: shaftUniform(shafts), pixelRatio: { value: pixelRatio } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      attribute float aSeed;
      uniform float time;
      uniform float pixelRatio;
      varying float vLight;
      varying float vTwinkle;
      ${SHAFT_GLSL(shafts.length)}
      void main() {
        vec3 p = position;
        float t = time * ( 0.05 + aSeed * 0.06 );
        p += vec3( sin( t * 3.0 + aSeed * 40.0 ), sin( t * 2.0 + aSeed * 17.0 ) * 0.6, cos( t * 2.5 + aSeed * 29.0 ) ) * 0.35;
        vLight = shaftLight( p );
        vTwinkle = 0.55 + 0.45 * sin( time * ( 1.5 + aSeed * 3.0 ) + aSeed * 60.0 );
        vec4 mv = modelViewMatrix * vec4( p, 1.0 );
        gl_PointSize = min( ( 1.5 + aSeed * 3.5 ) * pixelRatio * ( 3.0 / -mv.z ), 6.0 * pixelRatio );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float level;
      varying float vLight;
      varying float vTwinkle;
      void main() {
        float d = length( gl_PointCoord - 0.5 );
        float a = smoothstep( 0.5, 0.0, d );
        a *= mix( 0.03, 1.0, vLight ) * vTwinkle * level;
        gl_FragColor = vec4( vec3( 0.85, 0.92, 1.0 ) * a * 0.9, 1.0 );
      }`,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  points.renderOrder = 7;
  return points;
}

/**
 * A curtain of light under one tube: a wedge from the tube (narrow) to the
 * floor (wide), brightest at the top, soft at the edges.
 */
export function createCurtain(top, length, shared, strength = 1) {
  const g = new THREE.BoxGeometry(1, 1, 1, 1, 6, 1);
  const p = g.attributes.position;
  const h = top.y;
  for (let i = 0; i < p.count; i++) {
    const v = p.getY(i) + 0.5; // 0 floor .. 1 tube
    const w = THREE.MathUtils.lerp(1.7, 0.12, v);
    p.setXYZ(i, p.getX(i) * w, v * h, p.getZ(i) * length);
  }
  g.computeVertexNormals();
  const mat = new THREE.ShaderMaterial({
    uniforms: { ...shared, height: { value: h }, strength: { value: strength } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      varying float vH;
      varying vec3 vN;
      varying vec3 vP;
      uniform float height;
      void main() {
        vH = position.y / height;
        vec4 mv = modelViewMatrix * vec4( position, 1.0 );
        vP = mv.xyz;
        vN = normalize( normalMatrix * normal );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float level;
      uniform float strength;
      uniform float time;
      varying float vH;
      varying vec3 vN;
      varying vec3 vP;
      void main() {
        float facing = abs( dot( normalize( vN ), normalize( -vP ) ) );
        float body = pow( facing, 2.5 );
        float fall = pow( vH, 2.4 ) * smoothstep( 0.0, 0.12, vH );
        float drift = 0.85 + 0.15 * sin( vP.x * 3.0 + vH * 7.0 + time * 0.5 );
        float on = smoothstep( 0.5, 1.0, level );
        gl_FragColor = vec4( vec3( 0.7, 0.82, 1.0 ) * body * fall * drift * on * strength * 0.018, 1.0 );
      }`,
  });
  const mesh = new THREE.Mesh(g, mat);
  mesh.position.set(top.x, 0, top.z);
  mesh.renderOrder = 5;
  return mesh;
}

/** Final lens look, applied after tone mapping: subtle barrel, fringing, vignette, grain. */
const GradeShader = {
  name: 'AlphaDnaGrade',
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0 },
    grain: { value: 0.016 },
    lens: { value: 1 },
    resolution: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float grain;
    uniform float lens;
    uniform vec2 resolution;
    varying vec2 vUv;
    float hash( vec2 p ) {
      vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
      p3 += dot( p3, p3.yzx + 33.33 );
      return fract( ( p3.x + p3.y ) * p3.z );
    }
    void main() {
      vec2 c = vUv - 0.5;
      float r2 = dot( c, c );
      vec2 uv = 0.5 + c * ( 1.0 - 0.07 * r2 * lens );    // wide-lens barrel
      vec2 ca = c * ( 0.0025 + 0.006 * r2 ) * lens;        // fringing grows to the edges
      vec3 col = vec3(
        texture2D( tDiffuse, uv + ca ).r,
        texture2D( tDiffuse, uv ).g,
        texture2D( tDiffuse, uv - ca ).b
      );
      float vig = smoothstep( 0.85, 0.2, length( c * vec2( 1.0, 1.15 ) ) );
      col *= mix( 0.42, 1.0, vig );
      float g = hash( floor( vUv * resolution * 0.5 ) + floor( time * 12.0 ) * 1.7 ) - 0.5;
      col += g * grain * ( 1.0 - 0.6 * col );               // fine film grain, gentler in highlights
      gl_FragColor = vec4( col, 1.0 );
    }`,
};

export function createPost(renderer, scene, camera, opts) {
  const size = renderer.getSize(new THREE.Vector2());
  // Plain (non-multisampled) half-float target: MSAA on float buffers is where some
  // Windows/ANGLE drivers fall over. Edges are smoothed by FXAA at the end instead.
  const target = new THREE.WebGLRenderTarget(size.x, size.y, { type: THREE.HalfFloatType });
  const composer = new EffectComposer(renderer, target);
  composer.addPass(new RenderPass(scene, camera));
  // threshold sits above everything but the HDR tubes (and the hottest glints), so the room stays crisp
  const bloom = new UnrealBloomPass(new THREE.Vector2(size.x, size.y), 0.34, 0.2, 2.4);
  if (opts.bloom !== false) composer.addPass(bloom);
  composer.addPass(new OutputPass());
  const grade = new ShaderPass(GradeShader);
  grade.uniforms.grain.value = opts.grain ?? 0.016;
  grade.uniforms.lens.value = opts.lens ?? 1; // 0: no barrel or colour fringing (crisp lines)
  if (opts.grade !== false) composer.addPass(grade);
  const fxaa = new ShaderPass(FXAAShader);
  composer.addPass(fxaa);

  return {
    composer,
    setSize(w, h, pixelRatio) {
      composer.setPixelRatio(pixelRatio);
      composer.setSize(w, h);
      grade.uniforms.resolution.value.set(w * pixelRatio, h * pixelRatio);
      fxaa.uniforms.resolution.value.set(1 / (w * pixelRatio), 1 / (h * pixelRatio));
    },
    render(time) {
      grade.uniforms.time.value = time;
      composer.render();
    },
    dispose() {
      bloom.dispose();
      grade.dispose();
      fxaa.dispose();
      composer.dispose();
      target.dispose();
    },
  };
}
